import React from 'react';

interface AnalyticsDashboardSkeletonProps {
  layout: 'operational' | 'statewide';
}

const SKELETON_KPI_IDS = [
  'kpi-1',
  'kpi-2',
  'kpi-3',
  'kpi-4',
  'kpi-5',
  'kpi-6',
  'kpi-7',
  'kpi-8',
  'kpi-9',
  'kpi-10',
] as const;
const SKELETON_BAR_IDS = [
  'bar-1',
  'bar-2',
  'bar-3',
  'bar-4',
  'bar-5',
  'bar-6',
  'bar-7',
  'bar-8',
] as const;
const SKELETON_TAT_IDS = ['tat-1', 'tat-2', 'tat-3', 'tat-4', 'tat-5', 'tat-6'] as const;
const SKELETON_TABLE_COL_IDS = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9'] as const;
const SKELETON_TABLE_ROW_IDS = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8'] as const;

function SkeletonBlock({
  className = '',
  style,
}: Readonly<{
  className?: string;
  style?: React.CSSProperties;
}>) {
  return (
    <div
      className={`analytics-skeleton ${className}`.trim()}
      style={style}
      aria-hidden
    />
  );
}

function KpiCardsSkeleton({ count, gridClass }: { count: number; gridClass: string }) {
  return (
    <div className={gridClass}>
      {SKELETON_KPI_IDS.slice(0, count).map(id => (
        <div
          key={id}
          className="analytics-skeleton-kpi-card"
        >
          <SkeletonBlock className="analytics-skeleton-kpi-label" />
          <SkeletonBlock className="analytics-skeleton-kpi-value" />
          <SkeletonBlock className="analytics-skeleton-kpi-subtitle" />
        </div>
      ))}
    </div>
  );
}

function ChartCardSkeleton({ tall = false }: { tall?: boolean }) {
  return (
    <div
      className={`analytics-skeleton-chart-card ${tall ? 'analytics-skeleton-chart-card--tall' : ''}`}
    >
      <div className="analytics-skeleton-chart-header">
        <SkeletonBlock className="analytics-skeleton-chart-title" />
        <SkeletonBlock className="analytics-skeleton-chart-control" />
      </div>
      <div className="analytics-skeleton-chart-body">
        <SkeletonBlock className="analytics-skeleton-y-axis" />
        <div className="analytics-skeleton-bars">
          {SKELETON_BAR_IDS.map((id, offset) => (
            <SkeletonBlock
              key={id}
              className="analytics-skeleton-bar"
              style={{ height: `${35 + ((offset * 17) % 55)}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TatPanelSkeleton() {
  return (
    <div className="analytics-skeleton-chart-card analytics-skeleton-tat-card">
      <SkeletonBlock
        className="analytics-skeleton-chart-title"
        style={{ marginBottom: 16 }}
      />
      <div className="analytics-skeleton-tat-grid">
        {SKELETON_TAT_IDS.map(id => (
          <div
            key={id}
            className="analytics-skeleton-tat-item"
          >
            <SkeletonBlock className="analytics-skeleton-tat-label" />
            <SkeletonBlock className="analytics-skeleton-tat-value" />
          </div>
        ))}
      </div>
    </div>
  );
}

function TableSkeleton({ rows = 6, cols = 8 }: { rows?: number; cols?: number }) {
  return (
    <div className="analytics-skeleton-table-card">
      <div className="analytics-skeleton-table-header">
        <SkeletonBlock className="analytics-skeleton-table-title" />
        <SkeletonBlock className="analytics-skeleton-table-action" />
      </div>
      <div className="analytics-skeleton-table-head">
        {SKELETON_TABLE_COL_IDS.slice(0, cols).map(id => (
          <SkeletonBlock
            key={id}
            className="analytics-skeleton-table-th"
          />
        ))}
      </div>
      {SKELETON_TABLE_ROW_IDS.slice(0, rows).map(rowId => (
        <div
          key={rowId}
          className="analytics-skeleton-table-row"
        >
          {SKELETON_TABLE_COL_IDS.slice(0, cols).map(colId => (
            <SkeletonBlock
              key={`${rowId}-${colId}`}
              className="analytics-skeleton-table-td"
            />
          ))}
        </div>
      ))}
      <div className="analytics-skeleton-table-footer">
        <SkeletonBlock className="analytics-skeleton-footer-item" />
        <SkeletonBlock className="analytics-skeleton-footer-item" />
        <SkeletonBlock className="analytics-skeleton-footer-item" />
      </div>
    </div>
  );
}

export default function AnalyticsDashboardSkeleton({
  layout,
}: Readonly<AnalyticsDashboardSkeletonProps>) {
  const isOperational = layout === 'operational';

  return (
    <div
      className="analytics-skeleton-wrap"
      aria-busy="true"
      aria-label="Loading dashboard"
    >
      <KpiCardsSkeleton
        count={isOperational ? 8 : 10}
        gridClass={
          isOperational
            ? 'analytics-kpi-grid analytics-kpi-grid--operational'
            : 'analytics-kpi-grid analytics-kpi-grid--statewide'
        }
      />

      <div
        className={
          isOperational
            ? 'analytics-charts-row analytics-charts-row--operational'
            : 'analytics-charts-row analytics-charts-row--statewide'
        }
      >
        <ChartCardSkeleton tall />
        {isOperational ? <TatPanelSkeleton /> : <ChartCardSkeleton />}
      </div>

      <TableSkeleton
        rows={isOperational ? 8 : 6}
        cols={isOperational ? 9 : 8}
      />
    </div>
  );
}
